import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../config/api";
import { buildNavigation } from "../../../config/buildNavigation";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

const headClass =
  "mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#e0b65c]";
const linkClass =
  "text-sm text-[#e9dccb]/80 transition-colors hover:text-[#e0b65c]";

// "Company" links are static content pages (not product categories).
const company = [
  { name: "About Us", to: "/about" },
  { name: "Contact Us", to: "/contact" },
  { name: "Shipping & Delivery", to: "/shipping-policy" },
  { name: "Cancellation & Refunds", to: "/refund-policy" },
  { name: "Privacy Policy", to: "/privacy-policy" },
  { name: "Terms & Conditions", to: "/terms-and-conditions" },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(""); // "", "ok", "err"
  const [msg, setMsg] = useState("");
  const [s, setS] = useState({});
  const [shop, setShop] = useState([]);

  useEffect(() => {
    api.get("/api/settings").then(({ data }) => setS(data || {})).catch(() => {});
    // "Shop" links are the top-level categories from the DB — never hardcoded.
    api
      .get("/api/categories/menu")
      .then(({ data }) => {
        const nav = buildNavigation(data);
        if (!nav) return;
        // Each Shop link opens the whole category's product listing (all items
        // in it), matching the navbar tab click.
        setShop(
          nav.categories.map((cat) => ({
            name: cat.name,
            to: `/${cat.id}/${cat.id}/${cat.id}?view_all=true`,
          }))
        );
      })
      .catch(() => {});
  }, []);

  // Contact / social — admin-managed only, no hardcoded fallbacks.
  const contactEmail = s.contactEmail || "";
  const contactPhone = s.contactPhone || "";
  const storeHours = s.storeHours || "";
  const storeAddress = s.storeAddress || "";
  const brandName = s.brandName || "";
  const whatsappUrl = s.whatsappUrl || "";
  const socials = [
    { Icon: FacebookIcon, href: s.facebookUrl },
    { Icon: InstagramIcon, href: s.instagramUrl },
    { Icon: YouTubeIcon, href: s.youtubeUrl },
    { Icon: WhatsAppIcon, href: whatsappUrl },
  ].filter((x) => x.href);
  const hasContact = contactEmail || contactPhone || storeHours || storeAddress;
  const year = new Date().getFullYear();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      const { data } = await api.post("/api/newsletter", { email: email.trim() });
      setStatus("ok");
      setMsg(data?.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      setStatus("err");
      setMsg(err.response?.data?.error || "Could not subscribe.");
    }
  };

  return (
    <footer className="relative bg-gradient-to-b from-[#3d2417] to-[#251208] text-[#e9dccb]">
      {/* top accent line */}
      <div className="h-1 w-full bg-gradient-to-r from-[#b28585] via-[#e0b65c] to-[#b28585]" />

      <div className="mx-auto max-w-7xl px-6 pb-10 pt-14">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-5">
          {/* Brand + newsletter */}
          <div className="lg:col-span-2">
            {brandName && (
              <h2 className="font-display text-3xl font-bold text-[#f1e7da]">
                {brandName}
              </h2>
            )}
            {s.footerTagline && (
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#e9dccb]/70">
                {s.footerTagline}
              </p>
            )}

            <div className="mt-6">
              <p className={headClass}>Newsletter</p>
              <p className="mb-3 text-sm text-[#e9dccb]/70">
                New collections &amp; offers, before the rest.
              </p>
              <form
                onSubmit={handleSubscribe}
                className="flex max-w-md overflow-hidden rounded-full border border-white/20 bg-white/5"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full bg-transparent px-5 py-3 text-sm text-white placeholder-[#e9dccb]/50 focus:outline-none"
                />
                <button
                  type="submit"
                  className="shrink-0 bg-gradient-to-r from-[#f0ce82] to-[#cba24b] px-6 text-sm font-bold text-[#3d2417] transition-opacity hover:opacity-90"
                >
                  Subscribe
                </button>
              </form>
              {msg && (
                <p
                  className={`mt-2 text-xs ${
                    status === "ok" ? "text-[#9fe0bd]" : "text-[#e9b3b3]"
                  }`}
                >
                  {msg}
                </p>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              {socials.map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-[#f1e7da] transition-colors hover:bg-[#e0b65c] hover:text-[#3d2417]"
                >
                  <Icon fontSize="small" />
                </a>
              ))}
            </div>
          </div>

          {/* Shop */}
          {shop.length > 0 && (
            <div>
              <p className={headClass}>Shop</p>
              <ul className="space-y-3">
                {shop.map((l) => (
                  <li key={l.name}>
                    <Link to={l.to} className={linkClass}>
                      {l.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Company */}
          <div>
            <p className={headClass}>Company</p>
            <ul className="space-y-3">
              {company.map((l) => (
                <li key={l.name}>
                  <Link to={l.to} className={linkClass}>
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Reach Out */}
          {hasContact && (
            <div>
              <p className={headClass}>Reach Out</p>
              <ul className="space-y-4 text-sm text-[#e9dccb]/80">
                {contactEmail && (
                  <li className="flex items-start gap-2">
                    <EmailOutlinedIcon sx={{ fontSize: 18, color: "#e0b65c", mt: "2px" }} />
                    <a href={`mailto:${contactEmail}`} className="hover:text-[#e0b65c]">
                      {contactEmail}
                    </a>
                  </li>
                )}
                {contactPhone && (
                  <li className="flex items-start gap-2">
                    <WhatsAppIcon sx={{ fontSize: 18, color: "#e0b65c", mt: "2px" }} />
                    {whatsappUrl ? (
                      <a href={whatsappUrl} target="_blank" rel="noreferrer" className="hover:text-[#e0b65c]">
                        {contactPhone}
                      </a>
                    ) : (
                      <span>{contactPhone}</span>
                    )}
                  </li>
                )}
                {storeHours && (
                  <li className="flex items-start gap-2">
                    <AccessTimeRoundedIcon sx={{ fontSize: 18, color: "#e0b65c", mt: "2px" }} />
                    <span>{storeHours}</span>
                  </li>
                )}
                {storeAddress && (
                  <li className="flex items-start gap-2">
                    <PlaceOutlinedIcon sx={{ fontSize: 18, color: "#e0b65c", mt: "2px" }} />
                    <span>{storeAddress}</span>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-6 py-5 text-xs text-[#e9dccb]/60 sm:flex-row">
          <p>
            © {year}
            {s.footerCopyright ? ` ${s.footerCopyright}` : ""}
          </p>
          {s.footerBadge && (
            <p className="uppercase tracking-[0.15em]">{s.footerBadge}</p>
          )}
          <p>
            Designed &amp; Developed by{" "}
            <a
              href="https://elevontech.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#e9dccb]/80 transition-colors hover:text-[#e0b65c]"
            >
              Elevon Tech
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
